import { Download, RefreshCw, X } from 'lucide-react';
import { usePwa } from '../../hooks/usePwa';

/**
 * Discreet PWA UI (CLE-57):
 *   - a small "Install Firmio" banner (bottom of the screen) shown when
 *     the browser offers installation and the user hasn't dismissed it; and
 *   - an "Update available" toast when a new service worker is waiting.
 *
 * Both are non-modal and dismissible — no aggressive popups.
 */
export function PwaPrompts() {
  const { canInstall, promptInstall, dismissInstall, updateAvailable, update } = usePwa();

  if (!canInstall && !updateAvailable) return null;

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-center gap-2 p-4 pb-[calc(env(safe-area-inset-bottom)+1rem)]">
      {updateAvailable && (
        <div className="pointer-events-auto flex w-full max-w-sm items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-card-foreground shadow-lg">
          <RefreshCw className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">Update available</p>
            <p className="truncate text-xs text-muted-foreground">
              A new version of Firmio is ready.
            </p>
          </div>
          <button
            type="button"
            onClick={update}
            className="shrink-0 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90"
          >
            Refresh
          </button>
        </div>
      )}

      {canInstall && (
        <div className="pointer-events-auto flex w-full max-w-sm items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-card-foreground shadow-lg">
          <Download className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">Install Firmio</p>
            <p className="truncate text-xs text-muted-foreground">
              Add to your home screen for a faster, app-like experience.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void promptInstall()}
            className="shrink-0 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90"
          >
            Install
          </button>
          <button
            type="button"
            onClick={dismissInstall}
            aria-label="Dismiss install prompt"
            className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
}
